import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, CalendarClock } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { useConsultationModal } from "@/hooks/use-consultation-modal";

const Hero = () => {
  const { openConsultation } = useConsultationModal();
  return (
    <section className="relative min-h-[100svh] overflow-hidden">
      {/* Full-bleed background image — LCP element */}
      <div className="absolute inset-0">
        <picture>
          <source
            type="image/avif"
            srcSet="https://vibe.filesafe.space/1792357746068722743/assets/bc70fc58-7752-4f87-a621-5d23b9edf2da.avif 640w, https://vibe.filesafe.space/1792357746068722743/assets/e6c9832c-3893-43f3-9410-295b34817fa0.avif 960w, https://vibe.filesafe.space/1792357746068722743/assets/7b2b3379-32b7-4937-aea9-d0c4285f6820.avif 1280w, https://vibe.filesafe.space/1792357746068722743/assets/ece1b5f7-f53d-4224-8caa-1b2212f2f0ec.avif 1600w, https://vibe.filesafe.space/1792357746068722743/assets/7e72c4a7-8949-4247-afb6-427e323bc6f1.avif 1920w"
            sizes="100vw"
          />
          <source
            type="image/webp"
            srcSet="https://vibe.filesafe.space/1792357746068722743/assets/9f134c95-7010-41f6-8027-5abd2ea81a31.webp 640w, https://vibe.filesafe.space/1792357746068722743/assets/42f9db35-6d2e-4fbc-9eb3-56e20fac31b4.webp 960w, https://vibe.filesafe.space/1792357746068722743/assets/100589f4-dd23-45da-b58e-9e8afcc27912.webp 1280w, https://vibe.filesafe.space/1792357746068722743/assets/0984a03f-e066-43b5-b96b-f9d1dd719913.webp 1600w, https://vibe.filesafe.space/1792357746068722743/assets/1580405e-5003-4286-850b-e210d908e931.webp 1920w"
            sizes="100vw"
          />
          <img
            src="https://vibe.filesafe.space/1792357746068722743/assets/100589f4-dd23-45da-b58e-9e8afcc27912.webp"
            alt="Neuro Wellness TMS Centers of America — Main entrance in Coral Springs, FL"
            className="h-full w-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1600}
            height={915}
          />
        </picture>
        {/* Gradient overlay for text legibility — dark slate anchored left, fading to clear right */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-deep/95 via-primary-deep/70 to-transparent sm:via-primary-deep/55 sm:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/90 via-primary-deep/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex min-h-[100svh] items-center">
        <div
          className="container-px mx-auto max-w-7xl w-full py-20 sm:py-24 lg:py-0"
          style={{ paddingTop: "calc(env(safe-area-inset-top) + 5.5rem)" }}
        >
          <div className="max-w-2xl lg:max-w-[44rem] lg:pl-2">
            {/* Badge */}
            <Link
              to="/find-a-verified-saint-provider"
              className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-4 py-1.5 text-xs font-semibold text-accent backdrop-blur-sm transition-all hover:bg-accent/25 hover:border-accent sm:text-sm"
            >
              Verified SAINT® Provider · Coral Springs, Florida
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>

            {/* Main headline */}
            <h1 className="mt-4 font-headings text-[1.75rem] font-bold leading-[1.15] tracking-tight text-white xs:mt-5 xs:text-[2rem] sm:mt-6 sm:text-[2.5rem] md:text-[3rem] lg:mt-8 lg:text-[3.5rem] xl:text-[4rem]">
              Better Brain Health
              <span className="mt-0.5 block text-accent xs:mt-1 sm:mt-1.5">
                Starts Here.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/85 xs:mt-7 xs:text-lg sm:mt-7 sm:text-xl lg:mt-8 lg:text-2xl">
              Tried medications that didn’t work, or couldn’t live with the side
              effects? Our accelerated TMS treatments target the brain circuits
              behind depression, anxiety, OCD and PTSD, with no sedation, no
              downtime and a faster path to feeling like yourself again.
            </p>

            {/* CTA buttons */}
            <div className="mt-9 flex flex-col gap-3 sm:mt-11 sm:flex-row sm:items-center">
              <Button
                onClick={() => openConsultation("hero-primary")}
                size="lg"
                className="w-full sm:w-auto sm:min-w-[260px] bg-primary px-8 py-6 text-base font-bold text-white shadow-glow hover:brightness-110 sm:text-lg"
              >
                <CalendarClock className="h-5 w-5 mr-2 text-accent" />
                Request a Free Consultation
              </Button>
              <Button
                asChild
                size="lg"
                className="group gap-2 border-2 border-white/50 bg-white/5 px-6 py-6 text-base font-semibold text-white backdrop-blur-sm hover:bg-primary hover:border-primary sm:text-lg"
              >
                <Link to="/saint">
                  Explore SAINT® TMS
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
            <p className="mt-3 text-sm text-white/70">
              Prefer to call?{" "}
              <a
                href="tel:9548279793"
                className="font-semibold text-accent underline underline-offset-2 hover:text-white transition-colors"
              >
                954-827-9793
              </a>
            </p>

            {/* Trust row with SAINT by Magnus Logo */}
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3.5 rounded-2xl bg-primary-deep/60 p-3.5 backdrop-blur-md border border-white/15 max-w-xl">
              <div className="inline-flex items-center rounded-xl bg-white px-3 py-1.5 shadow-sm border border-border shrink-0 self-start sm:self-auto">
                <img
                  src="https://vibe.filesafe.space/1792357746068722743/assets/b6bccac9-f70e-417d-a62b-a963317bc613.webp"
                  srcSet="https://vibe.filesafe.space/1792357746068722743/assets/b6bccac9-f70e-417d-a62b-a963317bc613.webp 1x, https://vibe.filesafe.space/1792357746068722743/assets/f8b50c43-2bae-4ac4-9241-fabcb7829658.webp 2x"
                  alt="SAINT by Magnus Medical"
                  className="h-7 sm:h-8 w-auto object-contain"
                  width={72}
                  height={36}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm font-semibold text-white">
                <Link
                  to="/find-a-verified-saint-provider"
                  className="text-white underline decoration-accent/70 underline-offset-4 hover:text-accent drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)] transition-colors flex items-center gap-1.5"
                >
                  <div className="h-2 w-2 rounded-full bg-accent animate-pulse" />
                  Verified SAINT® Provider
                </Link>
                <div className="hidden h-1.5 w-1.5 shrink-0 rounded-full bg-accent sm:block" />
                <span className="text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                  Board-Certified Medical Director
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade into next section */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default Hero;
